/* https://nxt3.staging.lizard.net/api/v4/rasters/3e5f56a7-b16e-4deb-8449-cc2c88805159/point/?geom=POINT+(5.18292781406026+52.099018478979+0)&start=2015-01-01T01:00:00&stop=2021-05-11T09:00:00&statistic=mean&frequency=1d */

import { Point } from "geojson";
import { z } from "zod";

import { fetchWithError, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams, isAggregated, pointToWkt } from "@nens/shared-utils";

import { lizardDateSchema } from "../dates";
import { TimeseriesEvent } from "../types";

import { exportSchema } from "./timeseries_export";

const rasterPointSchema = z.object({
  results: z.array(
    z.object({
      time: lizardDateSchema.nullable(),
      value: z.number().nullable(),
    })
  ),
});

export type RasterPoint = z.infer<typeof rasterPointSchema>;

export function useNontemporalRasterPoint(uuid: string, geometry: Point) {
  const geom = pointToWkt(geometry);
  const url = combineUrlAndParams(`/api/v4/rasters/${uuid}/point/`, { geom });
  return useParsedQuery<RasterPoint>(
    ["lizard-api-v4", "rasterpoint", url],
    () => fetchWithError(url),
    (json: unknown) => rasterPointSchema.parse(json)
  );
}

export async function fetchTemporalRasterPoint(
  uuid: string,
  scale: string | null /* "ratio" | "interval" | "ordinal" */,
  geometry: Point,
  start: Date,
  end: Date,
  frequency: string,
  timesteps: number,
  params: Record<string, string | number> = {}
) {
  const geom = pointToWkt(geometry);
  const statistic = scale === "ratio" ? "sum" : scale === "ordinal" ? "median" : "mean";

  let timeseriesResults: TimeseriesEvent[] = [];
  const recursiveFetch = async (start: Date, end: Date) => {
    const url = combineUrlAndParams(`/api/v4/rasters/${uuid}/point/`, {
      start: start.toISOString(),
      stop: end.toISOString(),
      geom,
      ...(isAggregated(timesteps) && {
        statistic,
        frequency,
        closed: "right",
        label: "left",
      }),
      ...params,
    });

    const response = await fetch(url);
    const json = await response.json();

    // Split the request recursively if too many timesteps requested (> 10,000)
    if (json.status === 400 && json.code === 23) {
      const startMs = start.getTime();
      const endMs = end.getTime();
      const middleMs = startMs + (endMs - startMs) / 2;
      const middle = new Date(middleMs);

      await Promise.all([recursiveFetch(start, middle), recursiveFetch(middle, end)]);
      return;
    }

    if (!response.ok) throw new Error(`Received status: ${response.status}`);

    const results = rasterPointSchema.parse(json).results as TimeseriesEvent[];
    timeseriesResults = timeseriesResults.concat(results);
    return;
  };

  await recursiveFetch(start, end);
  return timeseriesResults;
}

export async function fetchTemporalRasterPointAsync(
  uuid: string,
  scale: string | null /* "ratio" | "interval" | "ordinal" */,
  geometry: Point,
  start: Date,
  end: Date,
  frequency: string,
  timesteps: number,
  params: Record<string, string | number> = {}
) {
  const geom = pointToWkt(geometry);
  const statistic = scale === "ratio" ? "sum" : scale === "ordinal" ? "median" : "mean";
  const url = combineUrlAndParams(`/api/v4/rasters/${uuid}/point/`, {
    async: true,
    start: start.toISOString(),
    stop: end.toISOString(),
    geom,
    ...(isAggregated(timesteps) && {
      statistic,
      frequency,
      closed: "right",
      label: "left",
    }),
    ...params,
  });

  const json = await fetchWithError(url);
  return exportSchema.parse(json);
}
